import { useEffect, useRef } from 'react';

/**
 * Goes back to the first page whenever `criteria` changes by content, e.g. the
 * search, filters and sort order that decide which rows match. The current
 * page may not exist in the new result, and page one is where a reader expects
 * a fresh search to start. A new but equal object does not trigger it, and
 * data changes alone (rows added or removed) keep the page.
 */
export function useFirstPageOnChange(
  criteria: unknown,
  pageIndex: number,
  goToFirstPage: () => void,
) {
  const criteriaKey = JSON.stringify(criteria);
  const previousCriteriaKey = useRef(criteriaKey);

  useEffect(() => {
    if (previousCriteriaKey.current === criteriaKey) return;
    previousCriteriaKey.current = criteriaKey;
    if (pageIndex !== 0) goToFirstPage();
  }, [criteriaKey, pageIndex, goToFirstPage]);
}

/**
 * When rows are removed, keeps pagination on the nearest valid page instead of
 * resetting to page one. With no rows left, that is page one. `pageCount` is
 * `undefined` while the total is unknown, which must not look like "no pages".
 */
export function useNearestValidPage(
  pageCount: number | undefined,
  pageIndex: number,
  goToPage: (pageIndex: number) => void,
) {
  useEffect(() => {
    if (pageCount === undefined) return;
    const lastPageIndex = Math.max(pageCount - 1, 0);
    if (pageIndex > lastPageIndex) goToPage(lastPageIndex);
  }, [pageCount, pageIndex, goToPage]);
}
